import { useEffect, useState } from "react";
import {
  fetchIntegrations,
  testIntegration,
  type Integration,
  type IntegrationTest,
  type Integrations,
} from "../api";
import { Icon } from "../components/Icon";

import { useT } from "../i18n";
const CATS: { id: Integration["category"]; label: string; ico: string }[] = [
  { id: "gateway", label: "Passerelle & crédits — APICreditForge / ComputeForge", ico: "route" },
  { id: "llm", label: "LLM — conception paramétrique & dialogue", ico: "cpu" },
  { id: "threed", label: "Génération 3D — idée/image → maillage", ico: "box" },
  { id: "image", label: "Génération d'image — pour image → 3D", ico: "image" },
  { id: "slicer", label: "Slicing", ico: "generators" },
  { id: "printer", label: "Imprimantes (réseau)", ico: "wifi" },
  { id: "storage", label: "Stockage", ico: "download" },
  { id: "observability", label: "Observabilité — Prometheus / Grafana", ico: "layers" },
  { id: "payment", label: "Paiement & marketplace", ico: "card" },
];

function intStatus(s: string, configured: boolean, tr: (x: string) => string) {
  if (configured && (s === "configured" || s === "ready")) return <span className="badge ok"><span className="dot" />{tr("Configuré")}</span>;
  if (s === "local") return <span className="badge ok"><span className="dot" />{tr("Local · prêt")}</span>;
  if (s === "available") return <span className="badge"><span className="dot" />Disponible</span>;
  if (s === "gpu") return <span className="badge warn"><span className="dot" />GPU requis</span>;
  return <span className="badge"><span className="dot" />{tr("À configurer")}</span>;
}

export function IntegrationsPage() {
  const tr = useT();
  const [data, setData] = useState<Integrations | null>(null);
  const [tests, setTests] = useState<Record<string, IntegrationTest>>({});
  const [testing, setTesting] = useState<string | null>(null);

  useEffect(() => {
    fetchIntegrations().then(setData).catch(() => {});
  }, []);

  async function runTest(id: string) {
    setTesting(id);
    try {
      const r = await testIntegration(id);
      setTests((t) => ({ ...t, [id]: r }));
    } catch {
      setTests((t) => ({ ...t, [id]: { id, configured: false, reachable: false, detail: "échec du test" } }));
    } finally {
      setTesting(null);
    }
  }

  if (!data) return <div className="muted">Chargement…</div>;
  const pct = Math.round((data.summary.configured / data.summary.total) * 100);

  return (
    <div>
      <div className="page-head">
        <div>
          <div className="eyebrow" style={{ marginBottom: 8 }}>{tr("Système")}</div>
          <h1>{tr("Intégrations & configuration")}</h1>
          <div className="sub">{tr("Tous les outils du pipeline, leur statut et leurs templates de config — prêts à brancher.")}</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="row between" style={{ marginBottom: 14 }}>
          <h2>Vue d'ensemble</h2>
          <span className="num muted">{data.summary.configured} / {data.summary.total} intégrations actives · {pct}%</span>
        </div>
        <div className="progress" style={{ marginBottom: 18, height: 8 }}><span style={{ width: `${pct}%` }} /></div>
        <div className="grid stats">
          {CATS.filter((c) => data.summary.by_category[c.id]).map((c) => (
            <div key={c.id} className="card stat" style={{ padding: 14 }}>
              <div className="ico"><Icon name={c.ico} /></div>
              <div className="v" style={{ fontSize: 24 }}>{data.summary.by_category[c.id]}</div>
              <div className="l" style={{ textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "var(--font-mono)", fontSize: 10.5 }}>{c.id}</div>
            </div>
          ))}
        </div>
      </div>

      {CATS.map((cat) => {
        const items = data.items.filter((i) => i.category === cat.id);
        if (items.length === 0) return null;
        return (
          <div key={cat.id} style={{ marginBottom: 28 }}>
            <div className="row" style={{ marginBottom: 14, color: "var(--text-2)", gap: 9 }}>
              <Icon name={cat.ico} size={16} /><h2>{cat.label}</h2>
            </div>
            <div className="grid cards">
              {items.map((it) => (
                <div key={it.id} className="card">
                  <div className="card-row" style={{ marginBottom: 9 }}>
                    <span className="card-title">{it.name}</span>
                    {intStatus(it.status, it.configured, tr)}
                  </div>
                  <div className="muted" style={{ fontSize: 12.5, minHeight: 40, lineHeight: 1.5 }}>{it.summary}</div>
                  {it.template.length > 0 && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--line)" }}>
                      <div className="eyebrow" style={{ marginBottom: 8 }}>{tr("Template de configuration")}</div>
                      <div className="col" style={{ gap: 6 }}>
                        {it.template.map((f) => (
                          <div key={f.key} className="row between" style={{ fontSize: 12 }}>
                            <span className="muted">{f.label}{f.secret && " 🔒"}</span>
                            <span className="kbd">{f.key}{f.placeholder ? ` = ${f.placeholder}` : ""}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="row between" style={{ marginTop: 12, paddingTop: 11, borderTop: "1px solid var(--line)" }}>
                    {tests[it.id] ? (
                      <span className={`badge ${tests[it.id].reachable === true ? "ok" : tests[it.id].reachable === false ? "bad" : ""}`} title={tests[it.id].detail}>
                        <span className="dot" />
                        {tests[it.id].reachable === true ? "Joignable" : tests[it.id].reachable === false ? "Injoignable" : "Vérifié"}
                      </span>
                    ) : <span className="muted" style={{ fontSize: 11.5 }}>{it.configured ? "Configuré" : "Non configuré"}</span>}
                    <button className="btn sm ghost" disabled={testing === it.id} onClick={() => runTest(it.id)}>
                      {testing === it.id ? <><span className="spinner" /> Test…</> : <><Icon name="zap" size={13} /> Tester</>}
                    </button>
                  </div>
                  {tests[it.id]?.detail && (
                    <div className="muted num" style={{ fontSize: 11, marginTop: 7 }}>{tests[it.id].detail}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
